import { Dialog } from "primereact/dialog";
import { Messages } from "primereact/messages";
import { ReactNode, useEffect, useRef, useState } from "react";
import { PrimeIcons } from "primereact/api";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { InputNumber } from "primereact/inputnumber";
import { Dropdown } from "primereact/dropdown";
import { Checkbox } from "primereact/checkbox";
import { BaseApiServiceImpl } from "../app_utils/api/BaseApiServiceImpl";
import { MessageUtils } from "../app_utils/utils/MessageUtils";

interface ModalType {
  children?: ReactNode;
  messageRef?: any;
  record: any;
  reloadFn: any;
  isOpen: boolean;
  toggle: () => void;
}

interface RecordType {
  id?: number;
  countryId: number;
  pricePerEmail: number;
  currencyCode: string;
  freeTierEnabled: boolean;
  freeTierLimit: number;
}

interface CountryType {
  id: number;
  name: string;
  currencyCode: string;
  currencySymbol: string;
}

const EmailMarkupFormDialog = (props: ModalType) => {
  const [record, setRecord] = useState<RecordType | null>(props?.record);
  const [countries, setCountries] = useState<CountryType[]>([]);
  const [selectedCurrency, setSelectedCurrency] = useState<string>("");
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const message = useRef<any>();

  const fetchCountriesFromServer = () => {
    new BaseApiServiceImpl("/api/v1/lookups/countries")
      .getRequestWithJsonResponse({ offset: 0, limit: 500 })
      .then(async (response) => {
        setCountries(response?.records || []);
      })
      .catch((error) => {
        MessageUtils.showErrorMessage(message, error.message);
      });
  };

  useEffect(() => {
    setRecord(props?.record);
    fetchCountriesFromServer();
  }, [props?.record]);

  const clearForm = () => {
    setRecord(null);
  };

  const validateForm = () => {
    if (!record?.countryId) {
      MessageUtils.showErrorMessage(message, "Please select a country");
      return false;
    }
    if (!record?.pricePerEmail || record.pricePerEmail <= 0) {
      MessageUtils.showErrorMessage(message, "Please enter a valid price per email");
      return false;
    }
    if (!record?.currencyCode) {
      MessageUtils.showErrorMessage(message, "Please select a currency");
      return false;
    }
    return true;
  };

  const saveRecord = () => {
    if (!validateForm()) return;
    setIsSaving(true);
    const url = props?.record?.id ? "/api/v1/email-markups/" + props.record.id : "/api/v1/email-markups";
    const method = props?.record?.id ? "putRequestWithJsonResponse" : "postRequestWithJsonResponse";

    new BaseApiServiceImpl(url)
      [method](record)
      .then((response) => {
        setIsSaving(false);
        MessageUtils.showSuccessMessage(message, response?.message || "Email markup saved successfully");
        clearForm();
        props.toggle();
        props.reloadFn();
      })
      .catch((error) => {
        setIsSaving(false);
        MessageUtils.showErrorMessage(message, error.message);
      });
  };

  const onCountryChange = (e: any) => {
    const country = countries.find((c) => c.id === e.value);
    setRecord((prev: any) => ({
      ...prev,
      countryId: e.value,
      currencyCode: country?.currencyCode || "",
    }));
    setSelectedCurrency(country?.currencyCode || "");
  };

  return (
    <Dialog
      header={props?.record?.id ? "Edit Email Markup" : "New Email Markup"}
      visible={props.isOpen}
      onHide={() => {
        clearForm();
        props.toggle();
      }}
      breakpoints={{ "960px": "75vw", "641px": "100vw" }}
      style={{ width: "50vw" }}
    >
      <Messages ref={message} />
      <div className="grid grid-cols-12 gap-4">
        <div className="col-12">
          <label htmlFor="country">Country *</label>
          <Dropdown
            id="country"
            value={record?.countryId}
            options={countries}
            optionLabel="name"
            optionValue="id"
            onChange={onCountryChange}
            className="w-full mt-1"
            placeholder="Select a country"
          />
        </div>
        <div className="col-12 md:col-6">
          <label htmlFor="pricePerEmail">Price Per Email *</label>
          <InputNumber
            id="pricePerEmail"
            value={record?.pricePerEmail}
            onValueChange={(e) => setRecord((prev: any) => ({ ...prev, pricePerEmail: e.value || 0 }))}
            className="w-full mt-1"
            minFractionDigits={2}
            maxFractionDigits={4}
          />
        </div>
        <div className="col-12 md:col-6">
          <label htmlFor="currencyCode">Currency Code *</label>
          <InputText
            id="currencyCode"
            value={record?.currencyCode || selectedCurrency}
            onChange={(e) => {
              setRecord((prev: any) => ({ ...prev, currencyCode: e.target.value }));
              setSelectedCurrency(e.target.value);
            }}
            className="w-full mt-1"
            placeholder="e.g., UGX, USD"
          />
        </div>
        <div className="col-12 md:col-6">
          <div className="flex align-items-center mt-3">
            <Checkbox
              inputId="freeTierEnabled"
              checked={record?.freeTierEnabled || false}
              onChange={(e) => setRecord((prev: any) => ({ ...prev, freeTierEnabled: e.checked ?? false }))}
            />
            <label htmlFor="freeTierEnabled" className="ml-2">
              Enable Free Tier
            </label>
          </div>
        </div>
        <div className="col-12 md:col-6">
          <label htmlFor="freeTierLimit">Free Tier Limit</label>
          <InputNumber
            id="freeTierLimit"
            value={record?.freeTierLimit}
            onValueChange={(e) => setRecord((prev: any) => ({ ...prev, freeTierLimit: e.value || 100 }))}
            className="w-full mt-1"
            min={1}
          />
        </div>
      </div>
      <div className="flex justify-content-end mt-4 gap-2">
        <Button label="Cancel" icon={PrimeIcons.TIMES} onClick={() => { clearForm(); props.toggle(); }} className="p-button-text" />
        <Button label="Save" icon={PrimeIcons.SAVE} onClick={saveRecord} loading={isSaving} />
      </div>
    </Dialog>
  );
};

export default EmailMarkupFormDialog;
